import React, { useEffect, useLayoutEffect, useRef, useState, useContext } from "react";
import "./AgendaDaySchedulesPopover.css";
import { createPortal } from "react-dom";
import { format } from "date-fns";
import { LuX } from "react-icons/lu";
import { Language } from "../../App";

const CARD_WIDTH = 183;
const VIEWPORT_GUTTER = 12;
const CARD_GAP = 10;

export default function AgendaDaySchedulesPopover({
  anchor,
  date,
  events,
  onClose,
  onSelectEvent,
  onShowSchedulePreview,
  onHideSchedulePreview,
}) {
  const { lang } = useContext(Language) || {};
  const popoverRef = useRef(null);
  const [position, setPosition] = useState(null);

  useLayoutEffect(() => {
    if (!anchor || !anchor.isConnected) return undefined;

    const updatePosition = () => {
      const anchorRect = anchor.getBoundingClientRect();
      const popoverHeight = popoverRef.current?.offsetHeight || 207;
      const maxLeft = window.innerWidth - CARD_WIDTH - VIEWPORT_GUTTER;
      const left = Math.min(
        Math.max(anchorRect.left, VIEWPORT_GUTTER),
        Math.max(VIEWPORT_GUTTER, maxLeft)
      );
      const topBelow = anchorRect.bottom + CARD_GAP;
      const hasSpaceBelow =
        topBelow + popoverHeight <= window.innerHeight - VIEWPORT_GUTTER;

      setPosition({
        left,
        top: hasSpaceBelow
          ? topBelow
          : Math.max(VIEWPORT_GUTTER, anchorRect.top - popoverHeight - CARD_GAP),
      });
    };

    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);

    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [anchor, date, events]);

  useEffect(() => {
    if (anchor?.isConnected) return undefined;
    onClose();
    return undefined;
  }, [anchor, onClose]);

  useEffect(() => {
    const closeOnEscape = (event) => {
      if (event.key === "Escape") onClose();
    };

    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [onClose]);

  if (!date || typeof document === "undefined") return null;

  return createPortal(
    <section
      ref={popoverRef}
      className="agenda-day-schedules-popover"
      style={position || { visibility: "hidden" }}
      role="dialog"
      aria-label={`Schedules for ${format(date, "d MMMM, yyyy")}`}
    >
      <div className="agenda-day-schedules-popover-header">
        <h4>{format(date, "d MMMM, yyyy")}</h4>
        <button type="button" onClick={onClose} aria-label="Close day schedules">
          <LuX aria-hidden="true" />
        </button>
      </div>

      {events.length > 0 ? (
        <div className="agenda-day-schedules-list">
          {events.map((event) => (
            <button
              key={event.id}
              type="button"
              className={`agenda-day-schedule-item schedule-type-${event.type}`}
              aria-label={event.title}
              onMouseEnter={(mouseEvent) =>
                onShowSchedulePreview(
                  event,
                  mouseEvent.currentTarget,
                  "side",
                  popoverRef.current
                )
              }
              onMouseLeave={onHideSchedulePreview}
              onFocus={(focusEvent) =>
                onShowSchedulePreview(
                  event,
                  focusEvent.currentTarget,
                  "side",
                  popoverRef.current
                )
              }
              onBlur={onHideSchedulePreview}
              onClick={() => onSelectEvent(event)}
            >
              <span>{event.title}</span>
            </button>
          ))}
        </div>
      ) : (
        <p className="agenda-day-schedules-empty">
          {lang?.no_schedules_this_day || "No schedules for this day."}
        </p>
      )}
    </section>,
    document.body
  );
}
